import round from 'lodash/round';
import { ComputeResourceType } from '@cast/shared-types';

import {
  computeResourceNameMap,
  computeResourceUnitsMap,
} from 'constants/maps';

import { DiffIcon } from 'features/cluster/workload-optimization/_components/DiffIcon';
import { OptimizationNotEnabledTooltip } from 'features/cluster/workload-optimization/_components/OptimizationNotEnabledTooltip';
import { ResourceMetrics } from 'features/workload-optimization';
import { Typography, Box, Stack, tokens } from '@cast/design-system';

type DiffValueProps = {
  metrics: ResourceMetrics;
  unit: ComputeResourceType;
};
const DiffValue = ({ metrics, unit }: DiffValueProps) => {
  if (metrics.diff === undefined) {
    return (
      <Box className="woop-premium-value" ml={24} data-testid={`${unit}-value`}>
        -
      </Box>
    );
  }
  return (
    <OptimizationNotEnabledTooltip unit={unit} wrapIf={metrics.isReadOnly}>
      <Stack data-testid={`${unit}-value`}>
        <Stack>
          <Typography
            variant="P10R"
            color={tokens.theme.fg.structural.primary}
            sx={{ textTransform: 'uppercase' }}
          >
            {computeResourceNameMap[unit]} difference:
          </Typography>
          <Stack direction="row" alignItems="center">
            <DiffIcon
              diff={metrics.diff}
              isReadOnly={metrics.isReadOnly}
              testId={`${unit}-icon`}
              size={16}
              sx={
                metrics.diff > 0
                  ? { color: tokens.theme.fg.structural.primary }
                  : undefined
              }
            />
            {!!metrics.diff && (
              <Stack direction="row" alignItems="baseline">
                <Typography
                  className="woop-premium-value"
                  variant="h6"
                  noWrap
                  {...{ [`${unit}-value`]: metrics.diff }}
                  color={
                    metrics.isReadOnly
                      ? tokens.theme.fg.structural.secondary
                      : 'inherit'
                  }
                >
                  {round(Math.abs(metrics.diff), 3)}
                </Typography>
                <Typography
                  variant="P10R"
                  pl={3}
                  textAlign="left"
                  color={tokens.theme.fg.structural.secondary}
                >
                  {computeResourceUnitsMap[unit]}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </OptimizationNotEnabledTooltip>
  );
};

type DiffValuesProps = {
  cpu: ResourceMetrics;
  memory: ResourceMetrics;
};

export const DiffValues = ({ cpu, memory }: DiffValuesProps) => {
  return (
    <Stack justifyContent="space-between" data-testid="diff-values">
      <DiffValue metrics={cpu} unit={ComputeResourceType.CPU} />
      <DiffValue metrics={memory} unit={ComputeResourceType.MEMORY} />
    </Stack>
  );
};
